"use client";

import * as React from "react";
import { Bell } from "lucide-react";
import { Button } from "../primitives/button";
import { cn } from "../primitives/cn";

export type NotificationBellItem = {
  id: string;
  title: string;
  body: string;
  readAt: string | null;
  createdAt: string;
};

export function NotificationBell({
  items,
  loading,
  title,
  empty,
  markReadLabel,
  ariaLabel,
  onMarkRead,
}: {
  items: NotificationBellItem[];
  loading?: boolean;
  title: string;
  empty: string;
  markReadLabel: string;
  ariaLabel: string;
  onMarkRead: (id: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const unread = items.filter((n) => !n.readAt).length;
  const panelId = "notification-inbox";

  return (
    <div className="relative">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-8 text-muted-foreground"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
      >
        <Bell className="size-4" />
        {unread > 0 ? (
          <span
            className="absolute right-1.5 top-1.5 size-[7px] rounded-full border-2 border-card bg-destructive"
            aria-hidden
          />
        ) : null}
      </Button>
      {open ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 cursor-default"
            aria-label={title}
            onClick={() => setOpen(false)}
          />
          <div
            id={panelId}
            role="region"
            aria-live="polite"
            className="absolute right-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-lg border border-border bg-card shadow-lg"
          >
            <p className="border-b border-border px-3 py-2 text-sm font-medium">
              {title}
            </p>
            {loading ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                …
              </p>
            ) : items.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                {empty}
              </p>
            ) : (
              <ul className="max-h-80 overflow-y-auto">
                {items.map((n) => (
                  <li
                    key={n.id}
                    className={cn(
                      "border-b border-border last:border-b-0",
                      !n.readAt && "bg-accent/40",
                    )}
                  >
                    <div className="space-y-1 px-3 py-2">
                      <p className="text-sm font-medium">{n.title}</p>
                      {n.body ? (
                        <p className="line-clamp-2 text-xs text-muted-foreground">
                          {n.body}
                        </p>
                      ) : null}
                      <p className="text-xs text-muted-foreground">
                        {new Date(n.createdAt).toLocaleString()}
                      </p>
                      {!n.readAt ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="min-h-8"
                          onClick={() => onMarkRead(n.id)}
                        >
                          {markReadLabel}
                        </Button>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
